/**
 * Volumen y cumplimiento de SLA por categoría.
 *
 * La categoría existía desde el vocabulario nuevo y no la mostraba ninguna pantalla: era un
 * campo del ticket y el eje que reparte los responsables, nada más. El framework del área la
 * pensaba como el agrupador con el que el gerente mira su portafolio —"¿en qué bucket cuento
 * esto?"— y ese bucket no se podía ver en ningún lado.
 *
 * Cada fila muestra los intents que la componen, que es lo que convierte "Fiscal: 41" en algo
 * accionable: 41 tickets fiscales no dicen nada; 30 de CAE y 11 de padrones, sí.
 */
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { analyticsAPI, capsulesAPI, capsuleConfigAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, BarChart3, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { unidadDeTrabajo } from '@/hooks/useCapsuleRoutingCatalogs';
import { BarraDeSemaforo } from '@/components/charts';

/**
 * **La barra del semáforo era una copia de `BarraDeSemaforo` (unificada el 2026-10-05).** Dos
 * componentes idénticos con paletas escritas a mano en cada uno: el día que el semáforo cambie de
 * color, uno de los dos se queda viejo y nadie se entera hasta que alguien compara dos pantallas.
 *
 * La copia hacía una cosa mejor —su `title` declaraba el universo— y eso se llevó al compartido.
 */
function Barra({ sla, total }) {
  const { ok = 0, risk = 0, breach = 0, sin_reloj: sinReloj = 0 } = sla || {};
  const abiertos = ok + risk + breach + sinReloj;
  return (
    <BarraDeSemaforo
      sla={sla}
      universo={`${abiertos} abiertos de ${total}`}
      vacio="Sin casos abiertos"
    />
  );
}

/**
 * `embebido` la vuelve una pestaña del Ejecutivo: sin encabezado propio y sin sus selectores,
 * que ahí los pone la pantalla de arriba. Era una página aparte a la que se llegaba por un
 * botón, y el corte por categoría es parte de mirar el tablero, no un desvío.
 */
export default function CategoriasPage({ embebido = false, capsuleId: capsulaProp, department: deptProp }) {
  const { user } = useAuth();
  const tenantId = user?.tenant_id;
  const [searchParams] = useSearchParams();

  const [capsules, setCapsules] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [unidad, setUnidad] = useState(null);
  const [capsuleId, setCapsuleId] = useState(
    capsulaProp ?? (searchParams.get('capsule') || searchParams.get('capsule_id') || '')
  );
  const [department, setDepartment] = useState(deptProp ?? (searchParams.get('department') || ''));

  // Embebida, los filtros los manda el Ejecutivo: si no siguiera sus cambios, las dos
  // mitades de la misma pantalla mostrarian universos distintos.
  useEffect(() => { if (embebido) setCapsuleId(capsulaProp || ''); }, [embebido, capsulaProp]);
  useEffect(() => { if (embebido) setDepartment(deptProp || ''); }, [embebido, deptProp]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    capsulesAPI.list(tenantId).then((r) => setCapsules(r.data || [])).catch(() => {});
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !capsuleId) { setDepartments([]); return; }
    capsuleConfigAPI.get(tenantId, capsuleId)
      .then((r) => {
        setDepartments(r.data?.requesting_departments || []);
        setUnidad(r.data?.unidad_de_trabajo || null);
      })
      .catch(() => setDepartments([]));
  }, [tenantId, capsuleId]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (capsuleId) params.capsule_id = capsuleId;
      if (department) params.department = department;
      const r = await analyticsAPI.pmoPorCategoria(params);
      setData(r.data);
    } catch {
      toast.error('No se pudo cargar el corte por categoría');
    } finally {
      setLoading(false);
    }
  }, [capsuleId, department]);

  useEffect(() => { cargar(); }, [cargar]);

  const filas = data?.categorias || [];
  const conTickets = filas.filter((f) => f.total > 0);
  const vacias = filas.filter((f) => f.total === 0);
  const maxTotal = Math.max(1, ...conTickets.map((f) => f.total));

  return (
    <div className={embebido ? 'space-y-4' : 'space-y-4'}>
      {!embebido && (
        <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold text-[#09090B]">Categorías</h1>
          <p className="text-xs text-zinc-500">
            Volumen y cumplimiento de SLA por bucket. El SLA sale del intent de cada ticket y se
            mide en horas hábiles.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/executive" className="text-xs text-brand-primary underline underline-offset-2">
            <BarChart3 className="w-3 h-3 inline mr-1" />Ejecutivo
          </Link>
          <Button size="sm" variant="outline" className="text-xs h-8 rounded-sm" onClick={cargar} disabled={loading}>
            <RefreshCw className="w-3 h-3 mr-1" /> Actualizar
          </Button>
        </div>
      </div>

      <Card className="border border-brand-border rounded-sm">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <select
            className="border border-brand-border rounded-sm text-xs h-8 px-2"
            value={capsuleId}
            onChange={(e) => setCapsuleId(e.target.value)}
            data-testid="categorias-capsula"
          >
            <option value="">Todas las cápsulas</option>
            {capsules.map((c) => (
              <option key={c.capsule_id} value={c.capsule_id}>{c.name || c.capsule_id}</option>
            ))}
          </select>
          <select
            className="border border-brand-border rounded-sm text-xs h-8 px-2"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            disabled={!departments.length}
          >
            <option value="">Todas las gerencias</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name || d.id}</option>
            ))}
          </select>
          {!capsuleId && (
            <span className="text-[11px] text-amber-700">
              Elegí una cápsula para ver los nombres de las categorías y el rol que las atiende.
            </span>
          )}
          <span className="text-[11px] text-zinc-500 ml-auto">
            {data?.total_tickets ?? 0} {unidadDeTrabajo(unidad, { plural: true }).toLowerCase()}
          </span>
        </CardContent>
      </Card>
        </>
      )}

      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 border-b border-brand-border">
          <CardTitle className="text-sm font-semibold uppercase tracking-[0.1em]">Por categoría</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
          ) : !conTickets.length ? (
            <p className="p-6 text-xs text-zinc-500">No hay {unidadDeTrabajo(unidad, { plural: true }).toLowerCase()} para este filtro.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 text-zinc-500">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Categoría</th>
                  <th className="text-right font-medium px-3 py-2 w-20">{unidadDeTrabajo(unidad, { plural: true })}</th>
                  <th className="text-right font-medium px-3 py-2 w-20">Abiertos</th>
                  <th className="text-left font-medium px-3 py-2 w-48">SLA de los abiertos</th>
                  <th className="text-left font-medium px-3 py-2">Qué hay adentro</th>
                  <th className="text-left font-medium px-3 py-2 w-48" title="El rol que viene atendiendo esta categoría, derivado de los casos que entraron">Rol que atiende</th>
                </tr>
              </thead>
              <tbody>
                {conTickets.map((f) => (
                  <tr key={f.id || 'sin'} className="border-t border-brand-border align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium text-[#09090B]">{f.name}</div>
                      {f.fuera_del_catalogo && (
                        <span className="text-[10px] text-amber-700 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> fuera del catálogo de la cápsula
                        </span>
                      )}
                      <div className="mt-1 h-1.5 bg-zinc-100 rounded-sm overflow-hidden w-32">
                        <div className="h-full bg-brand-primary" style={{ width: `${(f.total / maxTotal) * 100}%` }} />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{f.total}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{f.abiertos}</td>
                    <td className="px-3 py-2">
                      <Barra sla={f.sla} total={f.total} />
                      <div className="mt-1 text-[10px] text-zinc-500 flex gap-2">
                        {f.sla?.breach > 0 && <span className="text-red-600">{f.sla.breach} vencidos</span>}
                        {f.sla?.risk > 0 && <span className="text-amber-600">{f.sla.risk} en riesgo</span>}
                        {f.sla?.ok > 0 && <span className="text-emerald-700">{f.sla.ok} en plazo</span>}
                        {f.sla?.sin_reloj > 0 && <span>{f.sla.sin_reloj} sin reloj</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {f.intents.map((i) => (
                          <Badge key={i.id} variant="outline" className="text-[10px] font-normal" title={i.id}>
                            {i.name} · {i.n}
                          </Badge>
                        ))}
                        {!f.intents.length && <span className="text-[10px] text-zinc-400">sin intent</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-zinc-600">{f.rol_que_atiende || <span className="text-zinc-400" title="Todavía no entró ningún caso de esta categoría">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {!loading && vacias.length > 0 && (
        <Card className="border border-brand-border rounded-sm">
          <CardHeader className="pb-2 border-b border-brand-border">
            <CardTitle className="text-sm font-semibold uppercase tracking-[0.1em]">Sin {unidadDeTrabajo(unidad, { plural: true }).toLowerCase()}</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {/* Una categoría que nunca se usa es informacion, no un hueco: o el area no la
                necesita, o el clasificador no la elige nunca porque su descripcion no la
                distingue de la de al lado. */}
            <p className="text-[11px] text-zinc-500 mb-2">
              Categorías del vocabulario que todavía no recibieron ningún ticket. O el área no las
              usa, o el clasificador no las elige nunca: en ese caso, mirá su descripción.
            </p>
            <div className="flex flex-wrap gap-1">
              {vacias.map((f) => (
                <Badge key={f.id} variant="outline" className="text-[10px] font-normal" title={f.id}>{f.name}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
